import AppDialog from '@components/ui/AppDialog';
import { BodyText, Button } from '@kvdbil/components';
import { ButtonProps } from '@kvdbil/components/types/components/Button';
import { MouseEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const DialogText = styled(BodyText)`
  margin: 1rem 0;
`;

interface Props {
  buttonProps?: ButtonProps;
  dialogProps: {
    title: string;
    description: string;
    buttonProps?: Exclude<ButtonProps, 'onClick'> & {
      onClick?: CallableFunction;
    };
  };
}

const UtilityButton = ({ buttonProps, dialogProps }: Props) => {
  const { t } = useTranslation();
  const [isDialogOpen, setDialogOpen] = useState(false);

  const handleButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setDialogOpen(true);
      buttonProps?.onClick?.(event);
    },
    [buttonProps]
  );

  const handleDialogClick = useCallback((callback?: CallableFunction) => {
    if (callback) callback();
    setDialogOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  return (
    <>
      <Button
        size="small"
        variant="flat"
        color="error"
        {...buttonProps}
        onClick={event => handleButtonClick(event)}
      >
        {buttonProps?.children}
      </Button>

      <AppDialog
        isOpen={isDialogOpen}
        onClose={handleClose}
        title={dialogProps?.title}
        actions={
          <>
            <Button
              color="neutral"
              variant="flat"
              onClick={() => setDialogOpen(false)}
            >
              {t('Cancel')}
            </Button>
            <Button
              color="error"
              {...dialogProps?.buttonProps}
              data-testid={dialogProps?.buttonProps?.id}
              onClick={() =>
                handleDialogClick(dialogProps?.buttonProps?.onClick)
              }
            >
              {dialogProps?.buttonProps?.children}
            </Button>
          </>
        }
      >
        <DialogText>{dialogProps?.description}</DialogText>
      </AppDialog>
    </>
  );
};

export default UtilityButton;
